class FilterDrawer {
  constructor() {
    this.drawer = document.getElementById('FilterDrawer');
    this.trigger = document.getElementById('filter-icon');

    if (!this.drawer || !this.trigger) return;

    this.overlay = this.drawer.querySelector('.filter-drawer__overlay');
    this.panel = this.drawer.querySelector('.filter-drawer__panel');
    this.closeButton = this.drawer.querySelector('.filter-drawer__close');
    this.activeElement = null;
    this.isOpen = false;

    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onOverlayClick = this.onOverlayClick.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onPanelTransitionEnd = this.onPanelTransitionEnd.bind(this);

    this.mountToBody();
    this.bindEvents();
  }

  drawSelector(selector) {
    return this.drawer.querySelector(selector);
  }

  mountToBody() {
    if (this.drawer.parentElement !== document.body) {
      document.body.appendChild(this.drawer);
    }
    this.syncFormAssociation();
  }

  bindEvents() {
    if (this._eventsBound) return;
    this._eventsBound = true;

    this.trigger.addEventListener('click', this.onTriggerClick);

    if (this.overlay) {
      this.overlay.addEventListener('click', this.onOverlayClick);
    }

    this.drawer.querySelectorAll('[data-filter-drawer-close]').forEach((element) => {
      if (element === this.overlay) return;
      element.addEventListener('click', () => this.close());
    });

    this.drawer.addEventListener('keyup', this.onKeyUp);
    this.bindAccordionInteractions();
  }

  bindAccordionInteractions() {
    this.drawer.querySelectorAll('.filter-drawer .facets__disclosure').forEach((details) => {
      if (details.dataset.filterAccordionBound === 'true') return;
      details.dataset.filterAccordionBound = 'true';

      const summary = details.querySelector('summary');
      if (!summary) return;

      summary.addEventListener('click', (event) => {
        if (details.hasAttribute('open')) return;

        this.drawer.querySelectorAll('.filter-drawer .facets__disclosure[open]').forEach((openDetails) => {
          if (openDetails !== details) {
            openDetails.removeAttribute('open');
          }
        });
      });
    });
  }

  syncFormAssociation() {
    const formId = 'FacetFiltersForm';
    this.drawer.querySelectorAll('input, select, textarea').forEach((element) => {
      if (!element.getAttribute('form')) {
        element.setAttribute('form', formId);
      }
    });
  }

  reinit() {
    this.syncFormAssociation();
    this.bindAccordionInteractions();
  }

  onTriggerClick(event) {
    event.preventDefault();
    event.stopPropagation();
    this.isOpen ? this.close() : this.open();
  }

  onOverlayClick(event) {
    if (event.target === this.overlay) this.close();
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() === 'ESCAPE') this.close();
  }

  onPanelTransitionEnd(event) {
    if (event.target !== this.panel || !this.isOpen) return;

    if (typeof trapFocus === 'function') {
      trapFocus(this.panel, this.closeButton || this.panel);
    }
  }

  open() {
    if (this.isOpen) return;

    this.activeElement = document.activeElement;
    this.isOpen = true;

    this.drawer.classList.add('filter-drawer--active');
    this.drawer.setAttribute('aria-hidden', 'false');

    if (this.overlay) {
      this.overlay.setAttribute('aria-hidden', 'false');
    }

    document.body.classList.add('filter-drawer-open', 'overflow-hidden');
    this.trigger.setAttribute('aria-expanded', 'true');

    requestAnimationFrame(() => {
      this.drawer.classList.add('filter-drawer--animate');
    });

    this.panel?.addEventListener('transitionend', this.onPanelTransitionEnd, { once: true });
  }

  close() {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.drawer.classList.remove('filter-drawer--animate');

    const finishClose = () => {
      this.drawer.classList.remove('filter-drawer--active');
      this.drawer.setAttribute('aria-hidden', 'true');

      if (this.overlay) {
        this.overlay.setAttribute('aria-hidden', 'true');
      }

      document.body.classList.remove('filter-drawer-open', 'overflow-hidden');
      this.trigger.setAttribute('aria-expanded', 'false');

      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(this.activeElement || this.trigger);
      } else {
        (this.activeElement || this.trigger)?.focus();
      }
    };

    if (this.panel) {
      let closed = false;
      const safeFinish = () => {
        if (closed) return;
        closed = true;
        finishClose();
      };

      const onCloseTransition = (event) => {
        if (event.target !== this.panel) return;
        this.panel.removeEventListener('transitionend', onCloseTransition);
        safeFinish();
      };

      this.panel.addEventListener('transitionend', onCloseTransition);
      window.setTimeout(safeFinish, 500);
    } else {
      finishClose();
    }
  }
}

function initFilterDrawer() {
  if (!document.getElementById('FilterDrawer')) return;
  window.filterDrawerInstance = new FilterDrawer();
}

document.addEventListener('DOMContentLoaded', initFilterDrawer);
